// Library Methods
import { useEffect, useRef } from "react";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";

// Utils
import { webServiceUrl } from "src/utils/services/servicesHelper";

// Types
import { ExamSectionReportType } from "src/services/exams/types";

type UseLiveDataStreamType = {
  clinicId: string | undefined;
  deviceId: string | null;
  open: boolean;
  onSnapshot: (data: ExamSectionReportType) => void;
};

const useLiveDataStream = ({
  clinicId,
  deviceId,
  open,
  onSnapshot,
}: UseLiveDataStreamType) => {
  const { getToken } = useTokenAccess();

  // Keep the callback in a ref so changing it doesn't restart the stream.
  const onSnapshotRef = useRef(onSnapshot);
  onSnapshotRef.current = onSnapshot;

  useEffect(() => {
    if (!open || !deviceId || !clinicId) return;

    const controller = new AbortController();

    const startStream = async () => {
      try {
        const token = await getToken();

        const response = await fetch(
          `${webServiceUrl}/clinics/${clinicId}/devices/${deviceId}/live-data/stream`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          }
        );

        if (!response.ok || !response.body) {
          console.error(
            `Live data stream failed: ${response.status} ${response.statusText}`
          );
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let dataLines: string[] = [];

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Split on newlines but keep any incomplete trailing line in buffer.
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const raw of lines) {
            const line = raw.trimEnd();

            if (line.startsWith(":")) {
              // Keep-alive comment — ignore.
            } else if (line.startsWith("data:")) {
              dataLines.push(line.slice(5).trimStart());
            } else if (line === "") {
              // Empty line signals end of an event.
              if (dataLines.length > 0) {
                try {
                  const snapshot = JSON.parse(dataLines.join(""));
                  const payload = snapshot?.data ?? snapshot;
                  if (payload?.device?.id === deviceId) {
                    onSnapshotRef.current(payload);
                  }
                } catch {
                  // Malformed event — skip silently.
                }
                dataLines = [];
              }
            }
          }
        }
      } catch (e) {
        if ((e as Error).name !== "AbortError") {
          console.error("Live data stream error:", e);
        }
      }
    };

    startStream();

    return () => {
      controller.abort();
    };
  }, [clinicId, deviceId, open, getToken]);
};

export default useLiveDataStream;
